import { Outlet, createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import { FunnelLoader, type LoadingStep } from '../-funnel-components/funnel-loader';
import { FunnelSidebar } from '../-funnel-components/funnel-sidebar';
import { ContentHeader } from '../../../../components/content-header';
import { BlymLoading } from '../../../../components/loading';
import { MobileBlocker } from '../../../../components/mobile-blocker';
import { type Content } from '../../../../entity/api/content';
import { ContentAiFunnelStatus, type ContentFunnel } from '../../../../entity/api/content-funnel';
import { PlanStatus } from '../../../../entity/api/organization-billing';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { captureException } from '../../../../logger/capture-exception';
import { getQueryClient } from '../../../../sdk/request';
import {
  fetchArticleByIdImperative,
  getOrganizationBillingFromCache,
  getStores,
  useArticleAnalysisByArticleId,
  useArticleById,
  useArticleFunnelByArticleId,
  useOrganizationBilling,
} from '../../../../services/storage';
import { UIKey } from '../../../../services/storage/ui-key-value';
import { getAuthUserFromCache } from '../../../../services/storage/users-queries';
import { tGlobal } from '../../../../services/translation';
import { isLoading as isContentLoading } from '../../../../shared/content';
import { Feature, isFeatureAvailable } from '../../../../shared/feature';
import { usePostHogSessionRecording } from '../../../../shared/hooks/use-posthog-session-recording';
import { getSelectedOrganization } from '../../../../shared/organization';
import { isOrgCancelled } from '../../../../shared/subscription';

function computeLoadingState(content: Content | null, funnel: ContentFunnel | null) {
  if (content == null || funnel == null) {
    return false;
  }
  return isContentLoading(content, funnel);
}

export const Route = createFileRoute('/app/funnel/$id/_layout-funnel')({
  beforeLoad: async function (data: { params: { id: string } }) {
    addTrackCrumb({
      category: 'route',
      message: '/app/funnel/$id/_layout-funnel',
      level: 'info',
    });
    addTrackCrumb({
      category: 'funnel',
      message: 'inside funnel',
      level: 'info',
    });

    const { id } = data.params;

    // Get user from TanStack Query cache (populated during auth initialization)
    const queryClient = getQueryClient();
    const currentUser = getAuthUserFromCache(queryClient);
    const currentOrg = getSelectedOrganization(currentUser);
    const orgBilling = getOrganizationBillingFromCache(queryClient, currentOrg?._id ?? '');

    if (isOrgCancelled(orgBilling)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/app',
      });
    }

    try {
      await fetchArticleByIdImperative(queryClient, id);
    } catch (err) {
      captureException(
        err,
        {
          file: '_layout-funnel.tsx',
          method: 'loader',
          infoMessage: 'unable to get article by id',
          contentId: id,
        },
        trackError
      );

      throw err;
    }
  },
  // eslint-disable-next-line no-use-before-define
  component: Index,
});

function Index() {
  const { id } = Route.useParams() as unknown as { id: string };
  const router = useRouter();
  const navigate = useNavigate();
  const currentRoute = router.state.location.pathname ?? '';

  usePostHogSessionRecording();

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<LoadingStep>('title');
  const headerRef = useRef<HTMLHeadElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(57);

  // TanStack Query hooks
  const articleQuery = useArticleById(id);
  const article = articleQuery.data ?? null;
  const articleAnalysisQuery = useArticleAnalysisByArticleId(id);
  const articleAnalysis = articleAnalysisQuery.data ?? null;
  const articleFunnelQuery = useArticleFunnelByArticleId(id);
  const articleFunnel = articleFunnelQuery.data ?? null;

  // Other store data (uiKeyValue stays in Zustand as it's UI state)
  const isNotificationDismissed = getStores().uiKeyValue((s) =>
    s.getFromStore(UIKey.NOTIFICATION_DISMISSED)
  );
  const isTopBannerActive = getStores().uiKeyValue((s) =>
    s.getFromStore(UIKey.IS_TOP_BANNER_ACTIVE)
  );

  // TanStack Query for organization billing
  const { data: orgBillingStatus } = useOrganizationBilling(article?.organizationId);
  const isTopBarVisible =
    orgBillingStatus?.currentPlanStatus === PlanStatus.Cancelling ||
    orgBillingStatus?.currentPlanStatus === PlanStatus.PastDue;

  useEffect(
    function () {
      if (article != null && articleFunnel != null) {
        setIsInitialLoading(false);
      }
    },
    [article, articleFunnel]
  );

  useEffect(
    function () {
      if (articleFunnel?.status === ContentAiFunnelStatus.ReqGenContentLoad) {
        navigate({
          to: '/app/blogs',
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'funnel/$id/_layout-funnel',
              method: 'useEffect',
              infoMessage: 'unable to navigate to /app/blogs from funnel final generation',
              id,
            },
            trackError
          );
        });
      }
    },
    [articleFunnel, id, navigate]
  );

  const currentLoadingState = computeLoadingState(article, articleFunnel);

  // Update loading state synchronously
  useEffect(
    function () {
      setIsLoading(currentLoadingState);
    },
    [currentLoadingState]
  );

  useEffect(
    function () {
      if (article == null || articleFunnel == null) {
        return;
      }
      const loadingState = isContentLoading(article, articleFunnel);

      addTrackCrumb({
        category: 'funnel',
        message: `setting funnel to ${articleFunnel?.status}`,
        level: 'info',
      });

      // Update loading step based on content funnel status
      if (articleFunnel != null) {
        switch (articleFunnel.status) {
          case ContentAiFunnelStatus.Brief:
          case ContentAiFunnelStatus.ReqGenTitles:
          case ContentAiFunnelStatus.ReqGenTitlesLoad:
            setLoadingStep('title');
            break;
          case ContentAiFunnelStatus.ReqGenOutlines:
          case ContentAiFunnelStatus.ReqGenOutlinesLoad:
            setLoadingStep('outlines');
            break;
          case ContentAiFunnelStatus.ReqGenContent:
          case ContentAiFunnelStatus.ReqGenContentLoad:
            setLoadingStep('content');
            break;
          default:
            setLoadingStep('title');
            break;
        }
      }

      setIsLoading(loadingState);
    },
    [article, articleFunnel]
  );

  useEffect(
    function () {
      if (headerRef?.current?.clientHeight != null) {
        setHeaderHeight(headerRef.current.clientHeight);
      }
    },
    [headerRef]
  );

  useEffect(
    function () {
      if (isOrgCancelled(orgBillingStatus)) {
        window.location.href = '/app';
      }
    },
    [orgBillingStatus]
  );

  const getFunnelStep = function (route: string) {
    if (route === `/app/funnel/${id}/customize-content`) {
      return 1;
    }
    if (route === `/app/funnel/${id}/title`) {
      return 2;
    }
    if (route === `/app/funnel/${id}/outlines`) {
      return 2;
    }
    return 0;
  };

  const funnelStep = getFunnelStep(currentRoute);
  if (isInitialLoading) {
    return (
      <div className="mt-[-51px] flex h-full w-full flex-col items-center justify-center">
        <BlymLoading />
        <p className="font-body-2-r text-branding-gray">{tGlobal('loading')}</p>
      </div>
    );
  }

  return (
    <>
      <MobileBlocker />
      <ContentHeader
        ref={headerRef}
        isContentFunnel={true}
        content={article ?? undefined}
        onClose={function () {
          navigate({
            to: '/app/blogs',
          }).catch(function (err) {
            captureException(
              err,
              {
                file: 'funnel/$id.tsx',
                method: 'Index',
                infoMessage: 'unable to navigate to /app',
                id,
              },
              trackError
            );
          });
        }}
        savingState={'default'}
      />
      <main
        className="flex h-[calc(100vh-var(--header-height))]"
        style={
          {
            '--header-height': `${headerHeight + (!isNotificationDismissed && isTopBarVisible ? 44 : 0) + (isTopBannerActive === true ? 44 : 0)}px`,
          } as CSSProperties
        }
      >
        {isLoading ? (
          <FunnelLoader
            step={loadingStep}
            onBackToDashBoard={function () {
              void navigate({
                to: '/app/blogs',
              }).catch(function (err) {
                captureException(
                  err,
                  {
                    file: 'funnel/$id.tsx',
                    method: 'Index',
                    infoMessage: 'unable to navigate to /app',
                    id,
                  },
                  trackError
                );
              });
            }}
          />
        ) : (
          <>
            <Outlet />
            <div className="relative h-full w-contentSidebar border-l border-l-branding-gray-200 bg-branding-light-gray">
              {isFeatureAvailable(Feature.GEO_INFORMATION) && (
                <img
                  src="/geo-sidebar.jpg"
                  alt="Geo Information"
                  className="absolute right-0 top-0 z-0"
                />
              )}
              <FunnelSidebar
                contentId={id}
                contentAnalysis={articleAnalysis ?? undefined}
                funnelStep={funnelStep}
              />
            </div>
          </>
        )}
      </main>
    </>
  );
}
